import React, { memo, useMemo } from 'react';

import { Box, Link, Typography, useTheme } from '@/shared/ui';
import { AddToPhotosOutlined, LockOutlined } from '@/shared/ui/icons';

import type { ReactNode } from 'react';

import type { CatalogItem } from '@/entities/catalog-item';
import type { SymbolKey } from '@/entities/symbol';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import { getDiffColor } from '@/shared/lib/format/getDiffColor';
import Tooltip from '@/shared/ui/primitives/Tooltip';
import { ADDITIONAL_ITEM } from '../../../../constants';
import { SymbolCheckbox } from '../SymbolCheckbox/SymbolCheckbox';
import useCatalogTableCell from '../useCatalogTableCell';

import type { Cell } from '@tanstack/react-table';

const factsheetCellSx = { display: 'flex', justifyContent: 'center', width: '100%' };

const AdditionalItem = () => {
  return (
    <div style={{ width: '100%' }}>
      <Tooltip
        title={
          <>
            <Typography sx={{ fontSize: 14 }}>This GX code has additional items.</Typography>
            <Typography sx={{ fontSize: 14 }}>Check Display settings to view.</Typography>
          </>
        }
      >
        <span
          style={{
            display: 'inline-block',
            maxWidth: '100%'
          }}
        >
          <AddToPhotosOutlined sx={{ fontSize: '16px' }} />
        </span>
      </Tooltip>
    </div>
  );
};

const SpotCatalogCell = ({
  cell,
  isRestrictedData,
  symbol,
  isInDashboard,
  renderFactsheetButton
}: {
  cell: Cell<CatalogItem, unknown>;
  isRestrictedData: boolean;
  symbol?: SymbolKey;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}) => {
  const columnId = cell.column.id;
  const cellValue = cell.getValue() as string;

  const { palette } = useTheme();
  const { renderContent, defaultStyle } = useCatalogTableCell(columnId, cell);

  const restrictedTitle = useMemo(
    () => (
      <>
        You don't have access to this.{' '}
        <Link
          color='gx.text'
          href='https://www.general-index.com/contact-us'
          rel='noopener noreferrer'
          sx={{ fontWeight: 700 }}
          target='_blank'
        >
          Request access
        </Link>
      </>
    ),
    []
  );

  const returnCellColor = (isChangeColumn?: boolean, value?: string) => {
    if (isRestrictedData) return palette.table.infoRowText;
    if (isChangeColumn) return getDiffColor(Number(value));
    return 'inherit';
  };
  const spotStyle = {
    ...defaultStyle,
    justifyContent: ['Last', 'Change'].includes(columnId) ? 'flex-end' : 'flex-start',
    color: returnCellColor(columnId === 'Change', cellValue)
  };
  const renderCellContent = () => {
    switch (columnId) {
      case ADDITIONAL_ITEM: {
        if (!cell.row.original[ADDITIONAL_ITEM]) return <span />;
        return <AdditionalItem />;
      }
      case 'Symbol': {
        if (!symbol) return <span />;
        return (
          <SymbolCheckbox
            disabled={isRestrictedData}
            symbol={symbol}
          />
        );
      }
      case 'Index':
        return <span>{cellValue === 'Y' ? 'Index' : 'High, Mid, Low'}</span>;
      case 'Last':
      case 'Change':
        if (isRestrictedData) {
          return (
            <Tooltip title={restrictedTitle}>
              <LockOutlined
                color='inherit'
                sx={{ fontSize: '16px' }}
              />
            </Tooltip>
          );
        }
        return <span>{formatNumberWithPrecision(cellValue)}</span>;
      case 'Factsheet':
        return renderFactsheetButton ? (
          <Box sx={factsheetCellSx}>
            {renderFactsheetButton(cell.row.getValue('Code'), isInDashboard ? 'newTab' : 'modal')}
          </Box>
        ) : null;
      default:
        return <>{renderContent()}</>;
    }
  };

  return (
    <div
      key={cell.id + columnId}
      style={spotStyle}
    >
      {renderCellContent()}
    </div>
  );
};

export default memo(SpotCatalogCell);
